/**
 * Die Eingabezeile zeigt, was sie meint — fett ist fett, nicht `*fett*`.
 *
 * **Warum das nicht in einem `<textarea>` geht.** Ein Textfeld kann eine
 * Schrift haben, nicht zwei. Wer darin fett darstellen wollte, müsste hinter
 * das Feld eine zweite, gestaltete Fassung legen und das Feld selbst
 * durchsichtig machen — und dann laufen die beiden auseinander, sobald ein
 * Wort fett breiter ist als dasselbe Wort mager. Der Cursor stünde neben dem
 * Buchstaben. Das ist die bekannte Falle dieses Tricks, und sie ist nicht zu
 * umgehen.
 *
 * Also ein `contenteditable`. Damit kommen zwei Pflichten, und beide stehen
 * hier:
 *
 * - **Gespeichert wird weiter Text mit Zeichen**, `Das war *großartig*`.
 *   Nicht HTML. Der Grund steht in `auszeichnung.ts` und gilt unverändert:
 *   derselbe Text steht in der Vorschau der Chatliste und auf dem
 *   Sperrbildschirm, und dort gibt es keine Formatierung. `lese()` macht aus
 *   dem Feld wieder Zeichen, `schreibe()` aus Zeichen wieder ein Feld.
 * - **Was hineinkommt, ist Text.** Eingefügtes aus fremden Seiten bringt
 *   fremdes HTML mit; `alsText()` nimmt beim Einfügen ausschließlich die
 *   Zeichen. Ein Chat, in dem jemand Markup unterbringen kann, ist keiner —
 *   und das gilt beim Tippen genauso wie beim Anzeigen.
 */

import { zerlege, type Art } from './auszeichnung'

/** Welches Element welche Auszeichnung trägt. */
const VON_TAG: Record<string, Art> = {
  B: 'fett',
  STRONG: 'fett',
  I: 'kursiv',
  EM: 'kursiv',
  S: 'durch',
  STRIKE: 'durch',
  DEL: 'durch',
}

/** Und das Zeichen dazu. */
const ZEICHEN: Record<Exclude<Art, 'text'>, string> = {
  fett: '*',
  kursiv: '_',
  durch: '~',
  kapitael: '^',
}

/** Die Klasse für Kapitälchen — dafür gibt es kein eigenes Element. */
export const KAPITAEL = 'aus--kapitael'

/**
 * Was im Feld steht, als Text mit Zeichen.
 *
 * **Nicht verschachtelt**, wie in `auszeichnung.ts` festgelegt: liegt fett in
 * kursiv, gewinnt das äußere und das innere fällt weg. Ein Zerteiler, der
 * beides kann, wäre in einer Nachricht an Familie kein guter Handel — und
 * die halbe Umsetzung wäre schlimmer als keine.
 */
export function lese(wurzel: HTMLElement): string {
  let raus = ''

  const geheDurch = (knoten: Node, drin: boolean): void => {
    for (const kind of Array.from(knoten.childNodes)) {
      if (kind.nodeType === Node.TEXT_NODE) {
        raus += kind.textContent ?? ''
        continue
      }
      if (!(kind instanceof HTMLElement)) continue

      if (kind.tagName === 'BR') {
        raus += '\n'
        continue
      }

      // Safari packt jede weitere Zeile in ein `div`. Ein Umbruch davor, aber
      // nur einer -- sonst entstünde nach jedem Absatz eine Leerzeile.
      if (kind.tagName === 'DIV' || kind.tagName === 'P') {
        if (raus !== '' && !raus.endsWith('\n')) raus += '\n'
        geheDurch(kind, drin)
        continue
      }

      const art = artVon(kind)
      if (art === null || drin) {
        geheDurch(kind, drin)
        continue
      }

      const zeichen = ZEICHEN[art]
      const vorher = raus.length
      raus += zeichen
      geheDurch(kind, true)
      // Ein leeres Paar wäre nur ein Zeichenpaar im Text -- weg damit.
      if (raus.length === vorher + zeichen.length) raus = raus.slice(0, vorher)
      else raus += zeichen
    }
  }

  geheDurch(wurzel, false)
  return raus
}

/** Die Auszeichnung eines Elements — oder `null`. */
function artVon(el: HTMLElement): Exclude<Art, 'text'> | null {
  const vomTag = VON_TAG[el.tagName]
  if (vomTag !== undefined && vomTag !== 'text') return vomTag
  if (el.classList.contains(KAPITAEL)) return 'kapitael'
  // Der Browser setzt bei `styleWithCSS` mitunter einen Stil statt eines
  // Elements. Beides muss gelesen werden, sonst geht die Auszeichnung beim
  // Senden verloren.
  if (el.style.fontWeight === 'bold' || Number(el.style.fontWeight) >= 600) return 'fett'
  if (el.style.fontStyle === 'italic') return 'kursiv'
  if (el.style.textDecorationLine.includes('line-through')) return 'durch'
  if (el.style.fontVariantCaps !== '') return 'kapitael'
  return null
}

/**
 * Text mit Zeichen ins Feld schreiben — als gestaltete Stücke.
 *
 * Gebraucht beim Leeren nach dem Senden und beim Wiederherstellen eines
 * Entwurfs. Beim Tippen wird **nie** geschrieben: das Feld ist dann die
 * Wahrheit, und wer es aus dem Zustand nachzieht, setzt den Cursor ans Ende.
 */
export function schreibe(wurzel: HTMLElement, text: string): void {
  wurzel.replaceChildren()
  for (const stueck of zerlege(text)) {
    for (const [i, zeile] of stueck.text.split('\n').entries()) {
      if (i > 0) wurzel.append(document.createElement('br'))
      if (zeile === '') continue
      wurzel.append(stueck.art === 'text' ? zeile : gestaltet(stueck.art, zeile))
    }
  }
}

function gestaltet(art: Exclude<Art, 'text'>, text: string): HTMLElement {
  if (art === 'kapitael') {
    const span = document.createElement('span')
    span.className = KAPITAEL
    span.textContent = text
    return span
  }
  const el = document.createElement(art === 'fett' ? 'b' : art === 'kursiv' ? 'i' : 's')
  el.textContent = text
  return el
}

/**
 * Eingefügtes wird Text und sonst nichts.
 *
 * `insertText` und nicht `insertHTML`: was aus einer fremden Seite kommt,
 * bringt deren Markup mit — Verweise, Schriftgrößen, mitunter ein Skript.
 * Hier interessiert der Wortlaut.
 */
export function alsText(text: string): void {
  document.execCommand('insertText', false, text.replace(/\r\n?/g, '\n'))
}

/**
 * Eine Auszeichnung auf die Auswahl legen — oder wieder wegnehmen.
 *
 * **`execCommand` ist abgekündigt und trotzdem richtig hier.** Es ist das
 * einzige, was in jedem Browser die Auswahl korrekt trennt, den Cursor
 * behält und in den Rückgängig-Stapel des Browsers einträgt — das alles von
 * Hand mit `Range` nachzubauen wäre ein eigenes Projekt mit eigenen Fehlern.
 * Für Kapitälchen gibt es keinen Befehl; dort wird von Hand gehüllt.
 */
export function zeichneAus(art: Exclude<Art, 'text'>): void {
  if (art === 'kapitael') {
    kapitaelchen()
    return
  }
  document.execCommand(
    art === 'fett' ? 'bold' : art === 'kursiv' ? 'italic' : 'strikeThrough',
  )
}

/** Kapitälchen von Hand — hüllen, oder die Hülle wieder auflösen. */
function kapitaelchen(): void {
  const auswahl = window.getSelection()
  if (auswahl === null || auswahl.rangeCount === 0 || auswahl.isCollapsed) return
  const bereich = auswahl.getRangeAt(0)

  // Steckt die Auswahl schon in einer Hülle, wird sie aufgelöst -- derselbe
  // Knopf, zurück.
  const drin = (bereich.commonAncestorContainer instanceof HTMLElement
    ? bereich.commonAncestorContainer
    : bereich.commonAncestorContainer.parentElement
  )?.closest(`.${KAPITAEL}`)
  if (drin !== null && drin !== undefined) {
    const eltern = drin.parentNode
    if (eltern !== null) {
      while (drin.firstChild !== null) eltern.insertBefore(drin.firstChild, drin)
      eltern.removeChild(drin)
      eltern.normalize()
    }
    return
  }

  const huelle = document.createElement('span')
  huelle.className = KAPITAEL
  try {
    bereich.surroundContents(huelle)
  } catch {
    // Geht über Elementgrenzen hinweg nicht -- dann lieber nichts tun als
    // den Text zerlegen. Eine Auswahl innerhalb einer Zeile trifft es immer.
    return
  }
  auswahl.removeAllRanges()
  const neu = document.createRange()
  neu.selectNodeContents(huelle)
  auswahl.addRange(neu)
}

/** Ob die aktuelle Auswahl in diesem Feld liegt und etwas umfasst. */
export function auswahlIn(feld: HTMLElement | null): boolean {
  if (feld === null) return false
  const auswahl = window.getSelection()
  if (auswahl === null || auswahl.rangeCount === 0 || auswahl.isCollapsed) return false
  return feld.contains(auswahl.getRangeAt(0).commonAncestorContainer)
}
